<template>
  <BaseDialog centered contentClass="edit-category" ref="dialog" :show-actions="true">
    <template slot="title">{{ $t('page.admin-keywords.edit-keyword') }}</template>
    <template slot="content">
      <form @click.prevent @keydown.enter.prevent>
        <div>
          <FormInput
            ref="keywordValue"
            :label="$t('page.admin-keywords.keyword-value')"
            has-gray-background
            name="name"
            type="name"
            v-model="value"
            v-validate="'required'"
            @keydown="onKeydown"
          />
        </div>
        <div v-if="isFormInvalid">
          <KeywordsErrors :validation="keywordCriteria" singular color="error" />
        </div>
        <div class="edit-category__actions">
          <FormButton is-outlined class="edit-category__cancel" @click.native="cancelClick">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton
            @click.native="onSubmit"
            type="submit"
            :disabled="isFormInvalid || !hasChanged"
            :isLoading="submitting"
          >
            <span>{{ $t('dialog.filters.advanced.apply') }}</span>
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput.vue';
import KeywordsErrors from '@/components/util/KeywordsErrors';

import { mapActions } from 'vuex';
import { buildKeywords, validateKeywords } from '@/util/keywords-util';

export default {
  name: 'EditKeywordDialog',
  components: {
    BaseDialog,
    FormButton,
    FormInput,
    KeywordsErrors,
  },
  data() {
    return {
      value: '',
      keyword: null,
      submitting: false,
    };
  },
  props: {
    isDocumentKeyword: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isFormInvalid() {
      return !!!this.value || !this.keywordCriteria.valid;
    },
    hasChanged() {
      return !!this.keyword && this.value !== this.keyword.value;
    },
    keywordBuilded() {
      return buildKeywords(this.value);
    },
    keywordCriteria() {
      return validateKeywords(this.keywordBuilded);
    },
  },
  methods: {
    ...mapActions('keywords', ['editKeyword']),
    ...mapActions('documentKeywords', ['updateDocumentKeyword']),
    validateKeywords,
    openDialog(keyword) {
      this.value = keyword.value;
      this.keyword = keyword;
      this.submitting = false;
      this.$refs.dialog.openModal();

      this.$nextTick(() => {
        const refInput = this.$refs.keywordValue.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
    },
    // Enter in the field submits; it used to be a page-wide listener, which saved cancelled edits
    onKeydown(event) {
      if (event.key === 'Enter' && !event.repeat) this.onSubmit();
    },
    // Saves a real change once; a refused value keeps the dialog open to be fixed
    onSubmit() {
      if (this.submitting || this.isFormInvalid || !this.hasChanged) return;

      this.submitting = true;
      const saved = this.isDocumentKeyword
        ? this.updateDocumentKeyword({
            categoryId: this.keyword.categoryId,
            keywordId: this.keyword.id,
            updatedKeyword: this.value,
          })
        : this.editKeyword({
            keywordId: this.keyword.id,
            keyword: {
              id: this.keyword.id,
              tenantId: this.keyword.tenantId,
              categoryId: this.keyword.categoryId,
              value: this.value,
            },
          });

      return saved
        .then(() => {
          this.$toast.success(this.$t('page.admin-keywords.edit-keyword-success'));
          this.$emit('action-succeeded');
          this.closeDialog();
        })
        .catch((error) => this.$toast.error(error))
        .finally(() => {
          this.submitting = false;
        });
    },
    cancelClick() {
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss">
.base-dialog__edit-category {
  height: auto;
  width: 550px;
  position: relative;

  .base-dialog__content {
    padding: 32px;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}

.edit-category {
  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: 12px;
  }

  &__action {
    text-transform: lowercase;
    font-weight: bold;
  }
}
</style>
